import type { UiMessageModule } from "./merge";

export const publicDirectoryPageMessages = {
	en: {
		public: {
			providers: {
				metadata: {
					index: {
						title: "LLM Providers",
						description:
							"Browse 30+ LLM providers available through doteb - OpenAI, Anthropic, Google, Groq, Mistral, DeepSeek, xAI, and more. One API for all of them.",
						openGraphTitle: "LLM Providers - doteb",
						openGraphDescription:
							"Browse 30+ LLM providers available through doteb - OpenAI, Anthropic, Google, Groq, Mistral, DeepSeek, xAI, and more.",
						twitterTitle: "LLM Providers - doteb",
						twitterDescription:
							"Browse LLM providers available through one unified API.",
					},
					detail: {
						title: "{provider}",
						description:
							"Learn about {provider} integration with doteb. Access {provider} models through our unified API.",
						openGraphTitle: "{provider} - doteb",
						openGraphDescription:
							"Learn about {provider} integration with doteb. Access {provider} models through our unified API.",
						twitterTitle: "{provider} - doteb",
						twitterDescription:
							"Learn about {provider} integration with doteb.",
					},
				},
				og: {
					notFound: "Provider Not Found",
					provider: "Provider",
					modelAvailable: "model available",
					modelsAvailable: "models available",
					fallback: "doteb Provider",
				},
				grid: {
					title: "AI Providers",
					description:
						"Access {models} models from {providers} leading AI providers through our unified API",
					statsWindow: "Stats from last {window}",
					searchPlaceholder: "Search providers by name or description",
					sortBy: "Sort by",
					sort: {
						popular: "Most models",
						fastest: "Fastest (TTFT)",
						slowest: "Slowest (TTFT)",
						uptime: "Highest uptime",
						name: "Name (A-Z)",
					},
					noResults:
						'No providers match "{search}". Try a different search term.',
					noTraining: "No training",
					speed: {
						blazing: "Blazing",
						fast: "Fast",
						steady: "Steady",
						patient: "Patient",
					},
				},
				hero: {
					titleSuffix: "Provider",
					dataPrivacy: "Data & Privacy",
					hq: "HQ:",
					apiTraining: "API Training:",
					consumerTraining: "Consumer Training:",
					promptLogging: "Prompt Logging:",
					retention: "Retention:",
					gdpr: "GDPR:",
					soc2: "SOC2:",
					iso27001: "ISO 27001:",
					compliant: "Compliant",
					certified: "Certified",
					visitCompany: "Visit company",
					terms: "Terms of Service",
					privacy: "Privacy Policy",
				},
				detail: {
					availableModels: "Available Models",
				},
				descriptions: {
					llmgateway:
						"doteb is a framework for building and deploying large language models.",
					openai:
						"OpenAI is an AI research and deployment company building models for general intelligence.",
					anthropic:
						"Anthropic is a research and deployment company focused on safe and useful AI.",
					"google-ai-studio":
						"Google AI Studio provides access to Google's Gemini models.",
					glacier:
						"Glacier is a stealth provider with Google AI Studio-compatible Gemini endpoints.",
					"google-vertex":
						"Google Vertex AI provides access to Google's Gemini models through Vertex AI.",
					"vertex-openai":
						"Access partner models through Google Cloud Vertex AI's OpenAI-compatible Chat Completions endpoint.",
					"vertex-anthropic":
						"Access Claude models through Google Cloud Vertex AI with the Anthropic Messages API.",
					quartz:
						"Quartz is a Vertex-compatible provider for Gemini and other Vertex-routed models.",
					avalanche: "Avalanche is a video generation provider.",
					groq: "Groq provides ultra-fast LPU inference for multiple models.",
					cerebras:
						"Cerebras provides high-performance inference with ultra-fast throughput.",
					xai: "xAI provides Grok large language models.",
					deepseek:
						"DeepSeek provides high-performance language models with an OpenAI-compatible API.",
					alibaba:
						"Alibaba Bailian provides Qwen large language models through the DashScope OpenAI-compatible API.",
					novita: "NovitaAI provides OpenAI-compatible large language models.",
					"aws-bedrock":
						"Amazon Bedrock is a fully managed service for foundation models.",
					azure: "Microsoft Azure provides enterprise-grade OpenAI models.",
					"azure-ai-foundry":
						"Azure AI Foundry provides third-party models through the Azure Models inference endpoint.",
					zai: "Z AI provides OpenAI-compatible large language models.",
					moonshot:
						"Moonshot AI provides OpenAI-compatible large language models.",
					perplexity:
						"Perplexity provides AI models for search and conversation with real-time web access.",
					nebius:
						"Nebius AI Studio provides an OpenAI-compatible API for large language models.",
					mistral: "Mistral AI provides large language models.",
					canopywave:
						"CanopyWave runs large language models through an OpenAI-compatible API.",
					"inference-net":
						"Inference.net runs large language models in the cloud.",
					"together-ai":
						"Together AI runs large language models in the cloud with fast inference.",
					custom:
						"Custom OpenAI-compatible provider with configurable base URL.",
					nanogpt: "NanoGPT offers a large selection of models.",
					bytedance:
						"ByteDance's ModelArk platform provides an OpenAI-compatible API for large language models.",
					minimax:
						"MiniMax provides large language models with advanced reasoning and coding capabilities.",
					embercloud:
						"EmberCloud provides access to large language models through an OpenAI-compatible API.",
					xiaomi:
						"Xiaomi MiMo API Open Platform provides access to MiMo large language models.",
					deepinfra:
						"DeepInfra is an OpenAI-compatible inference platform for open-source models.",
					reve: "Reve provides image generation models with native 4K resolution and controllable image creation.",
					elevenlabs:
						"ElevenLabs provides lifelike, low-latency text-to-speech models in 70+ languages.",
				},
			},
			models: {
				metadata: {
					directory: {
						title: "AI Models Directory - Compare 180+ LLM Models",
						description:
							"Browse and compare 180+ AI models from leading providers like OpenAI, Anthropic, Google, and more. Filter by capabilities, pricing, and context size. Find the perfect LLM for your application.",
						openGraphTitle: "AI Models Directory - Compare 180+ LLM Models",
						openGraphDescription:
							"Browse and compare 180+ AI models from leading providers. Filter by capabilities, pricing, and context size.",
						twitterTitle: "AI Models Directory - Compare 180+ LLM Models",
						twitterDescription:
							"Browse and compare 180+ AI models from leading providers. Filter by capabilities, pricing, and context size.",
					},
					text: {
						title: "Text Generation Models - Chat, Code & Content LLMs",
						description:
							"Browse and compare text generation AI models from OpenAI, Anthropic, Google, and more. Find the best LLM for chat, code, and content generation.",
						openGraphTitle:
							"Text Generation Models - Chat, Code & Content LLMs",
						openGraphDescription:
							"Browse and compare text generation AI models from leading providers. Filter by pricing, context size, and capabilities.",
						twitterTitle: "Text Generation Models - Chat, Code & Content LLMs",
						twitterDescription:
							"Browse and compare text generation AI models from leading providers.",
					},
					vision: {
						title: "Vision Models - AI Models with Image Understanding",
						description:
							"Browse and compare AI models with vision capabilities. Analyze images, extract text, describe visuals, and more with multimodal LLMs.",
						openGraphTitle:
							"Vision Models - AI Models with Image Understanding",
						openGraphDescription:
							"Browse and compare AI models with vision capabilities. Analyze images, extract text, and describe visuals.",
						twitterTitle: "Vision Models - AI Models with Image Understanding",
						twitterDescription:
							"Browse and compare AI models with vision capabilities.",
					},
					reasoning: {
						title: "Reasoning Models - AI Models for Complex Analysis",
						description:
							"Browse and compare AI reasoning models like o1, o3, DeepSeek-R1, and more. Advanced chain-of-thought models for complex problem solving.",
						openGraphTitle: "Reasoning Models - AI Models for Complex Analysis",
						openGraphDescription:
							"Browse and compare AI reasoning models for complex problem solving and analysis.",
						twitterTitle: "Reasoning Models - AI Models for Complex Analysis",
						twitterDescription:
							"Browse and compare AI reasoning models for complex analysis.",
					},
					tools: {
						title: "Tool-Calling Models - AI Models with Function Calling",
						description:
							"Browse and compare AI models with tool and function calling support. Build agentic applications with models that can call APIs and execute functions.",
						openGraphTitle:
							"Tool-Calling Models - AI Models with Function Calling",
						openGraphDescription:
							"Browse and compare AI models with tool calling. Build agentic applications with function-calling capable LLMs.",
						twitterTitle:
							"Tool-Calling Models - AI Models with Function Calling",
						twitterDescription:
							"Browse AI models with tool and function calling support.",
					},
					webSearch: {
						title: "Web Search Models - AI Models with Internet Access",
						description:
							"Browse and compare AI models with built-in web search capabilities. Get real-time information and grounded responses from the internet.",
						openGraphTitle:
							"Web Search Models - AI Models with Internet Access",
						openGraphDescription:
							"Browse and compare AI models with native web search. Get real-time, grounded responses from the internet.",
						twitterTitle: "Web Search Models - AI Models with Internet Access",
						twitterDescription:
							"Browse AI models with native web search capabilities.",
					},
					textToImage: {
						title: "Text-to-Image Models - AI Image Generation",
						description:
							"Browse and compare AI image generation models like DALL-E, Flux, and more. Generate images from text prompts with multiple providers.",
						openGraphTitle: "Text-to-Image Models - AI Image Generation",
						openGraphDescription:
							"Browse and compare AI image generation models. Generate images from text prompts with multiple providers.",
						twitterTitle: "Text-to-Image Models - AI Image Generation",
						twitterDescription:
							"Browse AI image generation models for text prompts.",
					},
					imageToImage: {
						title: "Image-to-Image Models - AI Image Editing",
						description:
							"Browse and compare AI models that accept images as input and generate new images. Perfect for image editing, style transfer, and visual transformation.",
						openGraphTitle: "Image-to-Image Models - AI Image Editing",
						openGraphDescription:
							"Browse and compare AI models for image-to-image generation. Edit, transform, and enhance images with AI.",
						twitterTitle: "Image-to-Image Models - AI Image Editing",
						twitterDescription:
							"Browse AI models for image editing and transformation.",
					},
					video: {
						title: "Video Generation Models - AI Video Generation",
						description:
							"Browse and compare AI video generation models like Sora, Veo, and more. Generate videos from text prompts with multiple providers.",
						openGraphTitle: "Video Generation Models - AI Video Generation",
						openGraphDescription:
							"Browse and compare AI video generation models. Generate videos from text prompts with multiple providers.",
						twitterTitle: "Video Generation Models - AI Video Generation",
						twitterDescription:
							"Browse AI video generation models for text prompts.",
					},
					embedding: {
						title: "Embedding Models - AI Text Embeddings",
						description:
							"Browse and compare AI embedding models for semantic search, RAG, and similarity. Generate vector embeddings with multiple providers.",
						openGraphTitle: "Embedding Models - AI Text Embeddings",
						openGraphDescription:
							"Browse and compare AI embedding models for semantic search, RAG, and similarity with multiple providers.",
						twitterTitle: "Embedding Models - AI Text Embeddings",
						twitterDescription:
							"Browse AI embedding models for semantic search and RAG.",
					},
					discounted: {
						title: "Discounted Models - AI Models on Sale",
						description:
							"Browse and compare AI models with active discounts. Save on API costs with discounted pricing from multiple providers.",
						openGraphTitle: "Discounted Models - AI Models on Sale",
						openGraphDescription:
							"Browse and compare AI models with active discounts. Save on API costs with discounted pricing.",
						twitterTitle: "Discounted Models - AI Models on Sale",
						twitterDescription:
							"Browse AI models with active pricing discounts.",
					},
					compare: {
						title: "Compare AI Models Side by Side",
						description:
							"Select any two AI models to compare pricing, context window, and capabilities with our interactive model comparison tool.",
						openGraphTitle: "AI Model Comparison Tool",
						openGraphDescription:
							"Compare LLM pricing, context, and features across providers in a side-by-side view.",
						twitterTitle: "AI Model Comparison Tool",
						twitterDescription:
							"Compare LLM pricing, context, and features across providers in a side-by-side view.",
					},
					detail: {
						title: "{model} - AI Model Pricing & Capabilities",
						description:
							"Details, pricing, and capabilities for {model} on doteb.",
						ogAlt: "{model} model card",
					},
					providerDetail: {
						title: "{model} on {provider}",
						description:
							"Pricing, latency, and capabilities for {model} via {provider} on doteb.",
						ogAlt: "{model} on {provider} model card",
					},
					uptime: {
						title: "{model} Uptime & Latency - Live Provider Status",
						description:
							"Live {model} reliability across {providerCount} provider{plural}: uptime %, time-to-first-token, throughput, and error rates from the last 4 hours.",
						ogAlt: "{model} uptime status",
					},
				},
				og: {
					label: "Models",
					modelNotFound: "Model not found",
					modelAvailable: "model available",
					modelsAvailable: "models available",
					fallback: "doteb Model",
					family: "{family} family",
					pricingPerSecond: "Pricing per second",
					pricingPerRequest: "Pricing per request",
					pricingPerMillionTokens: "Pricing per 1M tokens",
					context: "Context",
					perSecond: "Per Second",
					perRequest: "Per Request",
					inputStartingAt: "Input (starting at)",
					outputStartingAt: "Output (starting at)",
					categories: {
						text: {
							title: "Text Generation",
							subtitle: "Chat, code, and content creation models",
						},
						"text-to-image": {
							title: "Text to Image",
							subtitle: "Generate images from text prompts",
						},
						"image-to-image": {
							title: "Image to Image",
							subtitle: "Edit and transform images with AI",
						},
						video: {
							title: "Video Generation",
							subtitle: "Generate videos from text prompts",
						},
						embedding: {
							title: "Embeddings",
							subtitle: "Vector embeddings for search and RAG",
						},
						"web-search": {
							title: "Web Search",
							subtitle: "Real-time internet-grounded responses",
						},
						vision: {
							title: "Vision",
							subtitle: "Analyze and understand images",
						},
						reasoning: {
							title: "Reasoning",
							subtitle: "Advanced chain-of-thought problem solving",
						},
						tools: {
							title: "Tool Calling",
							subtitle: "Function calling for agentic applications",
						},
						discounted: {
							title: "Discounted",
							subtitle: "Models with active pricing discounts",
						},
					},
				},
				directory: {
					title: "AI Models Directory",
					description:
						"Browse and compare 180+ AI models from OpenAI, Anthropic, Google, and 30+ providers - filter by capabilities, pricing, and context size.",
				},
				categories: {
					discounted: {
						title: "Discounted Models",
						description: "Models with active discounts - save on API costs",
					},
					text: {
						title: "Text Generation Models",
						description:
							"Models designed for text generation, chat, code, and content creation",
					},
					vision: {
						title: "Vision Models",
						description:
							"Models with image understanding capabilities - analyze, describe, and extract information from images",
					},
					reasoning: {
						title: "Reasoning Models",
						description:
							"Advanced chain-of-thought models for complex problem solving and analysis",
					},
					tools: {
						title: "Tool-Calling Models",
						description:
							"Models with function and tool calling support for building agentic applications",
					},
					webSearch: {
						title: "Web Search Models",
						description:
							"Models with built-in web search for real-time, internet-grounded responses",
					},
					textToImage: {
						title: "Text-to-Image Models",
						description: "Models that generate images from text prompts",
					},
					imageToImage: {
						title: "Image-to-Image Models",
						description:
							"Models that accept images as input and generate new images - for editing, style transfer, and visual transformation",
					},
					video: {
						title: "Video Generation Models",
						description: "Models that generate videos from text prompts",
					},
					embedding: {
						title: "Embedding Models",
						description: "Models that generate vector embeddings from text",
					},
				},
				compare: {
					title: "Compare AI Models Side by Side",
					description:
						"Select any two models to compare pricing, context window, and key capabilities across providers.",
				},
				detail: {
					backToProvider: "Back to {model}",
					embeddingModel: "Embedding model",
					viewUptime: "View uptime",
					free: "Free",
					tiered: "(tiered)",
					imageOutputTokens: "image output tokens",
					videoGeneration: "video generation",
					selectProvider: "Select Provider",
					allProvidersTitle: "All Providers for {model}",
					allProvidersDescription:
						"doteb routes requests to the best providers that are able to handle your prompt size and parameters.",
					providerPricingTitle: "{provider} Pricing for {model}",
					providerPricingDescription:
						"View detailed pricing and capabilities for this provider.",
					startingAt: "Starting at",
				},
				uptime: {
					title: "{model} - Uptime & Latency",
					description:
						"Real-time reliability for every provider serving {model} on doteb. Compare success rates, time-to-first-token, throughput, and error breakdown across {providerCount} provider{plural} so you can pick the fastest, most stable route for your workload.",
					perProvider: "Per provider",
					uptimeHint: "Share of requests with no upstream error",
					latencyValue: "TTFT + duration",
					latencyHint: "Time to first token and total duration in ms",
					throughputValue: "Tokens / sec",
					throughputHint: "Sustained generation speed across requests",
					errorsValue: "Client / gateway / upstream",
					errorsHint: "Failure source breakdown",
					performanceTitle: "Provider performance",
					performanceDescription:
						"Each card shows live traffic from the last 4 hours. Switch tabs to inspect requests, errors, latency, or token volume.",
					howTitle: "How doteb uses these metrics",
					howDescription:
						"Routing happens automatically - these charts show the data behind it.",
					howBody1:
						"Every {model} request flowing through doteb is scored on uptime, latency, and throughput. When an upstream provider degrades, traffic shifts to the next-best healthy endpoint without any client-side changes.",
					howBody2:
						"Use this page to verify SLA performance, debug regressions, or pick a primary provider for a self-hosted deployment.",
					faqTitle: "Frequently asked questions",
					faqMeasuredQuestion: "How is {model} uptime measured?",
					faqMeasuredAnswer:
						"Uptime is the share of requests that completed successfully on the upstream provider over the last 4 hours. Client errors (4xx from your request) and gateway errors are excluded so the number reflects the provider's reliability, not user errors.",
					faqProvidersQuestion: "Which providers serve {model}?",
					faqProvidersAnswer:
						"{model} is currently served by {providerCount} provider{plural}: {providers}. doteb routes requests to the best healthy provider in real time.",
					faqTtftQuestion: "What is TTFT and why does it matter?",
					faqTtftAnswer:
						"TTFT (time to first token) is the latency between the request and the first streamed token. Lower TTFT means the model starts responding faster - critical for chat UIs and agent loops.",
					faqUpdateQuestion: "How often does this page update?",
					faqUpdateAnswer:
						"Charts refresh every minute and aggregate the most recent 4 hours of traffic across all doteb projects. Data points are bucketed by minute.",
					datasetName: "{model} provider uptime - last 4 hours",
					datasetDescription:
						"Live request volume, error rates, latency (TTFT and total duration), and throughput for every provider serving {model} on doteb, refreshed every minute over the last 4 hours.",
				},
				card: {
					noProviders: "No providers available",
					unavailable:
						"This model is currently not available through any providers.",
					context: "Context:",
					inputShort: "in",
					outputShort: "out",
					perThousandRequests: "per 1K req",
					tieredPricing: "Tiered pricing available",
					seeDetails: "See more details",
					noRatings: "No ratings yet",
					rating: "rating",
					ratings: "ratings",
					signInToRate: "Sign in to rate",
					saveRating: "Save rating",
					saving: "Saving...",
					cancel: "Cancel",
					remove: "Remove",
					ratingPlaceholder: "Share your experience with this model (optional)",
				},
				discount: {
					off: "{percent}% off",
					thisModel: "this model",
					allModels: "all models",
					viaProvider: "via {provider}",
				},
			},
		},
	},
	"zh-CN": {
		public: {
			providers: {
				metadata: {
					index: {
						title: "LLM 供应商",
						description:
							"浏览 doteb 支持的 30+ LLM 供应商，包括 OpenAI、Anthropic、Google、Groq、Mistral、DeepSeek、xAI 等。一个 API 即可访问全部。",
						openGraphTitle: "LLM 供应商 - doteb",
						openGraphDescription:
							"浏览 doteb 支持的 30+ LLM 供应商，包括 OpenAI、Anthropic、Google、Groq、Mistral、DeepSeek、xAI 等。",
						twitterTitle: "LLM 供应商 - doteb",
						twitterDescription: "通过一个统一 API 访问多个 LLM 供应商。",
					},
					detail: {
						title: "{provider}",
						description:
							"了解 {provider} 与 doteb 的集成方式。通过统一 API 访问 {provider} 模型。",
						openGraphTitle: "{provider} - doteb",
						openGraphDescription:
							"了解 {provider} 与 doteb 的集成方式。通过统一 API 访问 {provider} 模型。",
						twitterTitle: "{provider} - doteb",
						twitterDescription: "了解 {provider} 与 doteb 的集成方式。",
					},
				},
				og: {
					notFound: "未找到供应商",
					provider: "供应商",
					modelAvailable: "个模型可用",
					modelsAvailable: "个模型可用",
					fallback: "doteb 供应商",
				},
				grid: {
					title: "AI 供应商",
					description:
						"通过统一 API 访问 {providers} 个领先 AI 供应商的 {models} 个模型",
					statsWindow: "统计窗口：最近 {window}",
					searchPlaceholder: "按名称或描述搜索供应商",
					sortBy: "排序",
					sort: {
						popular: "模型最多",
						fastest: "最快（TTFT）",
						slowest: "最慢（TTFT）",
						uptime: "可用率最高",
						name: "名称（A-Z）",
					},
					noResults: "没有供应商匹配“{search}”。请换个搜索词。",
					noTraining: "不训练",
					speed: {
						blazing: "极快",
						fast: "快速",
						steady: "稳定",
						patient: "较慢",
					},
				},
				hero: {
					titleSuffix: "供应商",
					dataPrivacy: "数据与隐私",
					hq: "总部：",
					apiTraining: "API 训练：",
					consumerTraining: "消费者产品训练：",
					promptLogging: "Prompt 记录：",
					retention: "保留周期：",
					gdpr: "GDPR：",
					soc2: "SOC2：",
					iso27001: "ISO 27001：",
					compliant: "合规",
					certified: "已认证",
					visitCompany: "访问公司官网",
					terms: "服务条款",
					privacy: "隐私政策",
				},
				detail: {
					availableModels: "可用模型",
				},
				descriptions: {
					llmgateway: "doteb 是用于构建和部署大型语言模型的框架。",
					openai: "OpenAI 是构建通用智能模型的 AI 研究与部署公司。",
					anthropic: "Anthropic 专注于构建安全且有用的 AI。",
					"google-ai-studio": "Google AI Studio 提供 Google Gemini 模型访问。",
					glacier: "Glacier 是兼容 Google AI Studio Gemini endpoint 的供应商。",
					"google-vertex":
						"Google Vertex AI 通过 Vertex AI 提供 Gemini 模型访问。",
					"vertex-openai":
						"通过 Google Cloud Vertex AI 的 OpenAI-compatible Chat Completions endpoint 访问合作伙伴模型。",
					"vertex-anthropic":
						"通过 Google Cloud Vertex AI 和 Anthropic Messages API 访问 Claude 模型。",
					quartz:
						"Quartz 是兼容 Vertex 的供应商，用于访问 Gemini 和其他 Vertex 路由模型。",
					avalanche: "Avalanche 是视频生成供应商。",
					groq: "Groq 为多种模型提供超高速 LPU 推理。",
					cerebras: "Cerebras 提供高性能推理和超高吞吐。",
					xai: "xAI 提供 Grok 大型语言模型。",
					deepseek: "DeepSeek 提供高性能语言模型和 OpenAI-compatible API。",
					alibaba:
						"阿里云百炼通过 DashScope 兼容 OpenAI 的 API 提供 Qwen 大型语言模型。",
					novita: "NovitaAI 提供 OpenAI-compatible 大型语言模型。",
					"aws-bedrock":
						"Amazon Bedrock 是面向 foundation models 的全托管服务。",
					azure: "Microsoft Azure 提供企业级 OpenAI 模型。",
					"azure-ai-foundry":
						"Azure AI Foundry 通过 Azure Models inference endpoint 提供第三方模型。",
					zai: "Z AI 提供 OpenAI-compatible 大型语言模型。",
					moonshot: "Moonshot AI 提供 OpenAI-compatible 大型语言模型。",
					perplexity:
						"Perplexity 提供面向搜索和对话、具备实时网络访问能力的 AI 模型。",
					nebius:
						"Nebius AI Studio 提供面向大型语言模型的 OpenAI-compatible API。",
					mistral: "Mistral AI 提供大型语言模型。",
					canopywave:
						"CanopyWave 通过 OpenAI-compatible API 运行大型语言模型。",
					"inference-net": "Inference.net 在云端运行大型语言模型。",
					"together-ai": "Together AI 在云端运行大型语言模型，并提供快速推理。",
					custom: "可配置 base URL 的自定义 OpenAI-compatible 供应商。",
					nanogpt: "NanoGPT 提供大量模型选择。",
					bytedance:
						"ByteDance 的 ModelArk 平台提供面向大型语言模型的 OpenAI-compatible API。",
					minimax: "MiniMax 提供具备高级推理和编码能力的大型语言模型。",
					embercloud:
						"EmberCloud 通过 OpenAI-compatible API 提供多种大型语言模型访问。",
					xiaomi: "Xiaomi MiMo API Open Platform 提供 MiMo 大型语言模型访问。",
					deepinfra: "DeepInfra 是面向开源模型的 OpenAI-compatible 推理平台。",
					reve: "Reve 提供原生 4K 分辨率和可控图像创作的图像生成模型。",
					elevenlabs:
						"ElevenLabs 提供覆盖 70+ 语言、低延迟且逼真的文本转语音模型。",
				},
			},
			models: {
				metadata: {
					directory: {
						title: "AI 模型目录 - 比较 180+ LLM 模型",
						description:
							"浏览并比较来自 OpenAI、Anthropic、Google 等领先供应商的 180+ AI 模型。按能力、价格和上下文长度筛选，找到适合应用的 LLM。",
						openGraphTitle: "AI 模型目录 - 比较 180+ LLM 模型",
						openGraphDescription:
							"浏览并比较来自领先供应商的 180+ AI 模型，可按能力、价格和上下文长度筛选。",
						twitterTitle: "AI 模型目录 - 比较 180+ LLM 模型",
						twitterDescription: "浏览并比较来自领先供应商的 180+ AI 模型。",
					},
					text: {
						title: "文本生成模型 - 聊天、代码与内容 LLM",
						description:
							"浏览并比较来自 OpenAI、Anthropic、Google 等供应商的文本生成 AI 模型，找到适合聊天、代码和内容生成的 LLM。",
						openGraphTitle: "文本生成模型 - 聊天、代码与内容 LLM",
						openGraphDescription:
							"浏览并比较来自领先供应商的文本生成 AI 模型，可按价格、上下文和能力筛选。",
						twitterTitle: "文本生成模型 - 聊天、代码与内容 LLM",
						twitterDescription: "浏览并比较文本生成 AI 模型。",
					},
					vision: {
						title: "视觉模型 - 具备图像理解能力的 AI 模型",
						description:
							"浏览并比较具备视觉能力的 AI 模型，可分析图像、提取文本、描述视觉内容等。",
						openGraphTitle: "视觉模型 - 具备图像理解能力的 AI 模型",
						openGraphDescription:
							"浏览并比较具备视觉能力的 AI 模型，可分析图像、提取文本并描述视觉内容。",
						twitterTitle: "视觉模型 - 具备图像理解能力的 AI 模型",
						twitterDescription: "浏览并比较具备视觉能力的 AI 模型。",
					},
					reasoning: {
						title: "推理模型 - 面向复杂分析的 AI 模型",
						description:
							"浏览并比较 o1、o3、DeepSeek-R1 等 AI 推理模型，用于复杂问题解决和分析。",
						openGraphTitle: "推理模型 - 面向复杂分析的 AI 模型",
						openGraphDescription:
							"浏览并比较面向复杂问题解决和分析的 AI 推理模型。",
						twitterTitle: "推理模型 - 面向复杂分析的 AI 模型",
						twitterDescription: "浏览并比较 AI 推理模型。",
					},
					tools: {
						title: "工具调用模型 - 支持 Function Calling 的 AI 模型",
						description:
							"浏览并比较支持工具和 function calling 的 AI 模型，用于构建 agentic 应用。",
						openGraphTitle: "工具调用模型 - 支持 Function Calling 的 AI 模型",
						openGraphDescription:
							"浏览并比较支持工具调用的 AI 模型，用于构建 agentic 应用。",
						twitterTitle: "工具调用模型 - 支持 Function Calling 的 AI 模型",
						twitterDescription: "浏览支持工具和 function calling 的 AI 模型。",
					},
					webSearch: {
						title: "网络搜索模型 - 具备互联网访问能力的 AI 模型",
						description:
							"浏览并比较内置网络搜索能力的 AI 模型，获取实时信息和基于互联网的回答。",
						openGraphTitle: "网络搜索模型 - 具备互联网访问能力的 AI 模型",
						openGraphDescription: "浏览并比较具备原生网络搜索能力的 AI 模型。",
						twitterTitle: "网络搜索模型 - 具备互联网访问能力的 AI 模型",
						twitterDescription: "浏览具备原生网络搜索能力的 AI 模型。",
					},
					textToImage: {
						title: "文生图模型 - AI 图像生成",
						description:
							"浏览并比较 DALL-E、Flux 等 AI 图像生成模型，通过多个供应商从文本 prompt 生成图像。",
						openGraphTitle: "文生图模型 - AI 图像生成",
						openGraphDescription:
							"浏览并比较 AI 图像生成模型，通过多个供应商从文本 prompt 生成图像。",
						twitterTitle: "文生图模型 - AI 图像生成",
						twitterDescription: "浏览根据文本 prompt 生成图像的 AI 模型。",
					},
					imageToImage: {
						title: "图生图模型 - AI 图像编辑",
						description:
							"浏览并比较接受图像输入并生成新图像的 AI 模型，适合图像编辑、风格迁移和视觉转换。",
						openGraphTitle: "图生图模型 - AI 图像编辑",
						openGraphDescription:
							"浏览并比较用于图生图生成、编辑、转换和增强的 AI 模型。",
						twitterTitle: "图生图模型 - AI 图像编辑",
						twitterDescription: "浏览用于图像编辑和转换的 AI 模型。",
					},
					video: {
						title: "视频生成模型 - AI 视频生成",
						description:
							"浏览并比较 Sora、Veo 等 AI 视频生成模型，通过多个供应商从文本 prompt 生成视频。",
						openGraphTitle: "视频生成模型 - AI 视频生成",
						openGraphDescription:
							"浏览并比较 AI 视频生成模型，通过多个供应商从文本 prompt 生成视频。",
						twitterTitle: "视频生成模型 - AI 视频生成",
						twitterDescription: "浏览根据文本 prompt 生成视频的 AI 模型。",
					},
					embedding: {
						title: "Embedding 模型 - AI 文本嵌入",
						description:
							"浏览并比较用于语义搜索、RAG 和相似度计算的 AI embedding 模型。",
						openGraphTitle: "Embedding 模型 - AI 文本嵌入",
						openGraphDescription:
							"浏览并比较用于语义搜索、RAG 和相似度计算的 AI embedding 模型。",
						twitterTitle: "Embedding 模型 - AI 文本嵌入",
						twitterDescription: "浏览用于语义搜索和 RAG 的 AI embedding 模型。",
					},
					discounted: {
						title: "折扣模型 - 降价中的 AI 模型",
						description:
							"浏览并比较当前有活动折扣的 AI 模型，通过多个供应商的折扣价格节省 API 成本。",
						openGraphTitle: "折扣模型 - 降价中的 AI 模型",
						openGraphDescription:
							"浏览并比较当前有活动折扣的 AI 模型，节省 API 成本。",
						twitterTitle: "折扣模型 - 降价中的 AI 模型",
						twitterDescription: "浏览当前有活动价格折扣的 AI 模型。",
					},
					compare: {
						title: "并排比较 AI 模型",
						description:
							"选择任意两个 AI 模型，使用交互式模型比较工具对比价格、上下文窗口和能力。",
						openGraphTitle: "AI 模型比较工具",
						openGraphDescription: "并排比较跨供应商的 LLM 价格、上下文和能力。",
						twitterTitle: "AI 模型比较工具",
						twitterDescription: "并排比较跨供应商的 LLM 价格、上下文和能力。",
					},
					detail: {
						title: "{model} - AI 模型价格与能力",
						description: "查看 {model} 在 doteb 上的详情、价格和能力。",
						ogAlt: "{model} 模型卡片",
					},
					providerDetail: {
						title: "{provider} 上的 {model}",
						description:
							"查看 {model} 通过 {provider} 在 doteb 上的价格、延迟和能力。",
						ogAlt: "{provider} 上的 {model} 模型卡片",
					},
					uptime: {
						title: "{model} 可用率与延迟 - 实时供应商状态",
						description:
							"实时查看 {model} 在 {providerCount} 个供应商上的可靠性：可用率、首 token 延迟、吞吐和最近 4 小时错误率。",
						ogAlt: "{model} 可用率状态",
					},
				},
				og: {
					label: "模型",
					modelNotFound: "未找到模型",
					modelAvailable: "个模型可用",
					modelsAvailable: "个模型可用",
					fallback: "doteb 模型",
					family: "{family} 系列",
					pricingPerSecond: "按秒计价",
					pricingPerRequest: "按请求计价",
					pricingPerMillionTokens: "按百万 token 计价",
					context: "上下文",
					perSecond: "按秒",
					perRequest: "每次请求",
					inputStartingAt: "输入（起价）",
					outputStartingAt: "输出（起价）",
					categories: {
						text: {
							title: "文本生成",
							subtitle: "聊天、代码和内容创作模型",
						},
						"text-to-image": {
							title: "文生图",
							subtitle: "根据文本 prompt 生成图像",
						},
						"image-to-image": {
							title: "图生图",
							subtitle: "用 AI 编辑和转换图像",
						},
						video: {
							title: "视频生成",
							subtitle: "根据文本 prompt 生成视频",
						},
						embedding: {
							title: "Embeddings",
							subtitle: "用于搜索和 RAG 的向量嵌入",
						},
						"web-search": {
							title: "网络搜索",
							subtitle: "基于实时互联网信息的回答",
						},
						vision: {
							title: "视觉",
							subtitle: "分析和理解图像",
						},
						reasoning: {
							title: "推理",
							subtitle: "高级 chain-of-thought 问题解决",
						},
						tools: {
							title: "工具调用",
							subtitle: "面向 agentic 应用的 function calling",
						},
						discounted: {
							title: "折扣",
							subtitle: "当前有价格折扣的模型",
						},
					},
				},
				directory: {
					title: "AI 模型目录",
					description:
						"浏览并比较来自 OpenAI、Anthropic、Google 和 30+ 供应商的 180+ AI 模型 - 可按能力、价格和上下文长度筛选。",
				},
				categories: {
					discounted: {
						title: "折扣模型",
						description: "当前有活动折扣的模型 - 节省 API 成本",
					},
					text: {
						title: "文本生成模型",
						description: "面向文本生成、聊天、代码和内容创作的模型",
					},
					vision: {
						title: "视觉模型",
						description: "具备图像理解能力的模型 - 可分析、描述并提取图像信息",
					},
					reasoning: {
						title: "推理模型",
						description: "面向复杂问题解决和分析的高级 chain-of-thought 模型",
					},
					tools: {
						title: "工具调用模型",
						description: "支持函数和工具调用，适合构建 agentic 应用的模型",
					},
					webSearch: {
						title: "网络搜索模型",
						description: "内置网络搜索能力，支持实时、基于互联网信息的回答",
					},
					textToImage: {
						title: "文生图模型",
						description: "根据文本 prompt 生成图像的模型",
					},
					imageToImage: {
						title: "图生图模型",
						description:
							"接受图像输入并生成新图像的模型 - 适合图像编辑、风格迁移和视觉转换",
					},
					video: {
						title: "视频生成模型",
						description: "根据文本 prompt 生成视频的模型",
					},
					embedding: {
						title: "Embedding 模型",
						description: "从文本生成向量嵌入的模型",
					},
				},
				compare: {
					title: "并排比较 AI 模型",
					description:
						"选择任意两个模型，比较价格、上下文窗口和跨供应商关键能力。",
				},
				detail: {
					backToProvider: "返回 {model}",
					embeddingModel: "Embedding 模型",
					viewUptime: "查看可用率",
					free: "免费",
					tiered: "（阶梯价）",
					imageOutputTokens: "图像输出 token",
					videoGeneration: "视频生成",
					selectProvider: "选择供应商",
					allProvidersTitle: "{model} 的所有供应商",
					allProvidersDescription:
						"doteb 会把请求路由到能够处理你的 prompt 长度和参数的最佳供应商。",
					providerPricingTitle: "{provider} 上 {model} 的价格",
					providerPricingDescription: "查看该供应商的详细价格和能力。",
					startingAt: "起价",
				},
				uptime: {
					title: "{model} - 可用率与延迟",
					description:
						"查看 doteb 上每个供应商服务 {model} 的实时可靠性。比较 {providerCount} 个供应商的成功率、首 token 延迟、吞吐和错误拆分，选择最快、最稳定的工作负载路由。",
					perProvider: "按供应商",
					uptimeHint: "无上游错误请求占比",
					latencyValue: "TTFT + 总耗时",
					latencyHint: "首 token 时间和总耗时（毫秒）",
					throughputValue: "Token / 秒",
					throughputHint: "请求中的持续生成速度",
					errorsValue: "客户端 / 网关 / 上游",
					errorsHint: "故障来源拆分",
					performanceTitle: "供应商性能",
					performanceDescription:
						"每张卡片展示最近 4 小时的实时流量。切换标签可查看请求、错误、延迟或 token 量。",
					howTitle: "doteb 如何使用这些指标",
					howDescription: "路由会自动发生 - 这些图表展示背后的数据。",
					howBody1:
						"每个通过 doteb 的 {model} 请求都会按可用率、延迟和吞吐评分。当上游供应商退化时，流量会切到下一个健康 endpoint，无需客户端修改。",
					howBody2:
						"你可以用这个页面验证 SLA 表现、排查回归，或为自托管部署选择主供应商。",
					faqTitle: "常见问题",
					faqMeasuredQuestion: "{model} 的可用率如何计算？",
					faqMeasuredAnswer:
						"可用率是最近 4 小时内上游供应商成功完成请求的占比。客户端错误（你的请求产生的 4xx）和网关错误会被排除，因此该数字反映供应商可靠性，而不是用户错误。",
					faqProvidersQuestion: "哪些供应商提供 {model}？",
					faqProvidersAnswer:
						"{model} 当前由 {providerCount} 个供应商提供：{providers}。doteb 会实时把请求路由到最健康的供应商。",
					faqTtftQuestion: "TTFT 是什么，为什么重要？",
					faqTtftAnswer:
						"TTFT（time to first token）是请求发出到首个流式 token 返回之间的延迟。更低的 TTFT 意味着模型开始响应更快，这对聊天 UI 和 agent 循环非常关键。",
					faqUpdateQuestion: "这个页面多久更新一次？",
					faqUpdateAnswer:
						"图表每分钟刷新一次，并聚合所有 doteb 项目最近 4 小时的流量。数据点按分钟分桶。",
					datasetName: "{model} 供应商可用率 - 最近 4 小时",
					datasetDescription:
						"实时请求量、错误率、延迟（TTFT 和总耗时）以及服务 {model} 的每个供应商在 doteb 上的吞吐数据，每分钟刷新并覆盖最近 4 小时。",
				},
				card: {
					noProviders: "暂无可用供应商",
					unavailable: "该模型当前没有任何供应商可用。",
					context: "上下文：",
					inputShort: "输入",
					outputShort: "输出",
					perThousandRequests: "每 1K 次请求",
					tieredPricing: "支持阶梯价",
					seeDetails: "查看更多详情",
					noRatings: "暂无评分",
					rating: "条评分",
					ratings: "条评分",
					signInToRate: "登录后评分",
					saveRating: "保存评分",
					saving: "保存中...",
					cancel: "取消",
					remove: "移除",
					ratingPlaceholder: "分享你使用该模型的体验（可选）",
				},
				discount: {
					off: "{percent}% 折扣",
					thisModel: "此模型",
					allModels: "全部模型",
					viaProvider: "通过 {provider}",
				},
			},
		},
	},
} satisfies UiMessageModule;
